import { createServerFn } from "@tanstack/react-start";
import {
  createAuthenticatedApiConfig,
  throwApiError,
  hasServerReadPermission,
  hasServerDeletePermission,
} from "@/lib/utils.server";
import {
  createOrganisation,
  createCommonContact,
  createServiceContact,
  getCommonContacts,
  getCommonContact,
  getOrganisation,
  getOrganisationPredefinedTexts,
  getOrganisations,
  getOrganisationSettings,
  getOrganisationUserLabels,
  getServiceContacts,
  getServiceContact,
  CommonContactResponseReadable,
  ServiceContactResponseReadable,
  updateCommonContact,
  updateServiceContact,
  deleteServiceContact,
  deleteCommonContact,
  updateOrganisationPredefinedTexts,
  deleteOrganisation,
  updateOrganisation,
  updateOrganisationSettings,
  deleteAllOrganisationSettings,
  getOrganisationCallscreening,
  updateOrganisationCallscreening,
  testOrganisationCallscreening,
  updateOrganisationUserLabels,
  deleteOrganisationSetting,
} from "@/lib/mma";
import {
  OrganisationReadable,
  PredefinedTextsResponse,
  SettingListResponse,
  UserLabelList,
  CallscreeningResponse,
  CallscreeningTestResponse,
} from "@/lib/mma/types.gen";
import { isForbiddenError } from "@/lib/utils";

//TODO: Standardize the SubmenuItem type so that it can be used across all components
export const getOrganisationsFn = createServerFn({ method: "GET" }).handler(async (): Promise<any> => {
  const config = await createAuthenticatedApiConfig();
  const response = await getOrganisations(config);
  if (!response.data) {
    return await throwApiError(response, "Failed to get organisations", response.response?.status);
  }
  const organisations = response.data?.data || [];

  const hasSettingsPermission = await hasServerReadPermission("SETTINGS");
  const hasCallscreeningPermission = await hasServerReadPermission("CALL_SCREENING");
  const hasContactPermission = await hasServerReadPermission("CONTACT_LIST");
  const hasPredefinedTextPermission = await hasServerReadPermission("PREDEFINED_TEXT");
  const hasUserLabelPermission = await hasServerReadPermission("USER_LABEL");
  const hasUserPermission = await hasServerReadPermission("USER");
  const hasDeletePermissionOrganisation = await hasServerDeletePermission("ORGANISATION");

  return organisations.map((organisation: any) => {
    const baseDropdown = [
      ...(hasSettingsPermission
        ? [{ id: `${organisation.id}-settings`, name: "Settings", href: `/organisations/${organisation.id}/settings` }]
        : []),
      ...(hasContactPermission
        ? [
            {
              id: `${organisation.id}-contact-list`,
              name: "Contact list",
              href: `/organisations/${organisation.id}/contact-list`,
            },
          ]
        : []),
      ...(hasPredefinedTextPermission
        ? [
            {
              id: `${organisation.id}-predefined-texts`,
              name: "Predefined texts",
              href: `/organisations/${organisation.id}/predefined-texts`,
            },
          ]
        : []),
      ...(hasUserPermission
        ? [
            {
              id: `${organisation.id}-users`,
              name: "Users",
              href: `/organisations/${organisation.id}/users`,
            },
          ]
        : []),
      {
        id: `${organisation.id}-rename`,
        name: "Rename",
        href: `/organisations/${organisation.id}/rename`,
      },
      ...(hasUserLabelPermission
        ? [
            {
              id: `${organisation.id}-user-labels`,
              name: "User labels",
              href: `/organisations/${organisation.id}/user-labels`,
            },
          ]
        : []),
      {
        id: `${organisation.id}-emails`,
        name: "Emails",
        href: `/organisations/${organisation.id}/emails`,
      },
      ...(hasCallscreeningPermission
        ? [
            {
              id: `${organisation.id}-call-screening`,
              name: "Call screening",
              href: `/organisations/${organisation.id}/call-screening`,
            },
          ]
        : []),
      ...(hasDeletePermissionOrganisation
        ? [
            {
              id: `${organisation.id}-delete`,
              name: "Delete",
              onClick: "delete",
              color: "red-500",
              parent: { name: organisation.name, id: organisation.id },
            },
          ]
        : []),
    ];

    return {
      id: organisation.id,
      name: organisation.name,
      href: `/organisations/${organisation.id}`,
      dropdown: baseDropdown,
    };
  });
});

//There are settings that rely on the query key being exactly "organisations", so don't change it unless it is changed in settings. See settings-fields.tsx for more details.
export const organisationsQueryOptions = () => ({
  queryKey: ["organisations"],
  queryFn: async () => {
    try {
      return await getOrganisationsFn();
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") {
        return;
      }
      throw e;
    }
  },
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: (query: any) => (isForbiddenError(query.state.error) ? false : "always"),
  refetchOnReconnect: false,
  retry: false,
});

export const createOrganisationFn = createServerFn({ method: "POST" })
  .inputValidator((data: { name: string }) => data)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await createOrganisation({
      ...config,
      body: {
        name: data.name,
      },
    });
    if (!response.data) {
      return await throwApiError(response, "Failed to create organisation", response.response?.status);
    }
    return response.data;
  });

export const getOrganisationFn = createServerFn({ method: "GET" })
  .inputValidator((d: { orgId: number }) => d)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await getOrganisation({
      ...config,
      path: {
        id: Number(data.orgId),
      },
    });
    if (!response.data) {
      return await throwApiError(response, "Failed to get organisation", response.response?.status);
    }
    return response.data?.data || {};
  });

export function organisationQueryOptions(orgId: number) {
  return {
    queryKey: ["organisation", orgId],
    queryFn: () => getOrganisationFn({ data: { orgId } }),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
    refetchOnMount: (query: any) => (isForbiddenError(query.state.error) ? false : "always"),
    refetchOnReconnect: false,
    retry: false,
  };
}

export const getPredefinedTextsFn = createServerFn({ method: "GET" })
  .inputValidator((d: { orgId: number }) => d)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await getOrganisationPredefinedTexts({
      ...config,
      path: {
        id: Number(data.orgId),
      },
    });
    if (!response.data) {
      return await throwApiError(response, "Failed to get predefined texts", response.response?.status);
    }
    return response.data?.data || {};
  });

export function predefinedTextsQueryOptions(orgId: number) {
  return {
    queryKey: ["predefinedTexts", orgId],
    queryFn: () => getPredefinedTextsFn({ data: { orgId } }),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
    refetchOnMount: (query: any) => (isForbiddenError(query.state.error) ? false : "always"),
    refetchOnReconnect: false,
    retry: false,
  };
}

export const getOrganisationUserLabelsFn = createServerFn({ method: "GET" })
  .inputValidator((data: { orgId: number }) => data)
  .handler(async ({ data }): Promise<any> => {
    const config = await createAuthenticatedApiConfig();
    const response = await getOrganisationUserLabels({
      ...config,
      path: { id: data.orgId },
    });
    if (!response.data) {
      return await throwApiError(response, "Failed to get organisation user labels", response.response?.status);
    }
    return response.data?.data || [];
  });

export const organisationUserLabelsQueryOptions = (orgId: number) => ({
  queryKey: ["organisationUserLabels", orgId],
  queryFn: () => getOrganisationUserLabelsFn({ data: { orgId } }),
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: (query: any) => (isForbiddenError(query.state.error) ? false : "always"),
  refetchOnReconnect: false,
  retry: false,
});

export const fetchOrganisationSettingsFn = createServerFn({ method: "GET" })
  .inputValidator((data: { orgId: number }) => data)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await getOrganisationSettings({ ...config, path: { id: Number(data.orgId) } });
    if (!response.data) {
      return await throwApiError(response, "Failed to get organisation settings", response.response?.status);
    }
    return {
      settings: response.data?.data || {},
    };
  });

export const getCommonContactsFn = createServerFn({ method: "GET" })
  .inputValidator((data: { orgId: number }) => data)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await getCommonContacts({ ...config, path: { id: Number(data.orgId) } });
    if (!response.data) {
      return await throwApiError(response, "Failed to get common contacts", response.response?.status);
    }
    return response.data?.data || [];
  });

export const getServiceContactsFn = createServerFn({ method: "GET" })
  .inputValidator((data: { orgId: number }) => data)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await getServiceContacts({ ...config, path: { id: Number(data.orgId) } });
    if (!response.data) {
      return await throwApiError(response, "Failed to get service contacts", response.response?.status);
    }
    return response.data?.data || [];
  });

export const commonContactsQueryOptions = (orgId: number) => ({
  queryKey: ["commonContacts", orgId],
  queryFn: () => getCommonContactsFn({ data: { orgId } }),
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: (query: any) => (isForbiddenError(query.state.error) ? false : "always"),
  refetchOnReconnect: false,
  retry: false,
});

export const serviceContactsQueryOptions = (orgId: number) => ({
  queryKey: ["serviceContacts", orgId],
  queryFn: () => getServiceContactsFn({ data: { orgId } }),
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: (query: any) => (isForbiddenError(query.state.error) ? false : "always"),
  refetchOnReconnect: false,
  retry: false,
});

export const getCommonContactFn = createServerFn({ method: "GET" })
  .inputValidator((data: { orgId: number; contactId: number }) => data)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await getCommonContact({
      ...config,
      path: { id: data.orgId, contactId: data.contactId },
    });
    if (!response.data) {
      return await throwApiError(response, "Failed to get common contact", response.response?.status);
    }
    return response.data?.data || {};
  });

export const commonContactQueryOptions = (orgId: number, contactId: number) => ({
  queryKey: ["commonContact", orgId, contactId],
  queryFn: () => getCommonContactFn({ data: { orgId, contactId } }),
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: (query: any) => (isForbiddenError(query.state.error) ? false : "always"),
  refetchOnReconnect: false,
  retry: false,
});

export const getServiceContactFn = createServerFn({ method: "GET" })
  .inputValidator((data: { orgId: number; contactId: number }) => data)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await getServiceContact({
      ...config,
      path: { id: data.orgId, contactId: data.contactId },
    });
    if (!response.data) {
      return await throwApiError(response, "Failed to get service contact", response.response?.status);
    }
    return response.data?.data || {};
  });

export const serviceContactQueryOptions = (orgId: number, contactId: number) => ({
  queryKey: ["serviceContact", orgId, contactId],
  queryFn: () => getServiceContactFn({ data: { orgId, contactId } }),
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: (query: any) => (isForbiddenError(query.state.error) ? false : "always"),
  refetchOnReconnect: false,
  retry: false,
});

export const createCommonContactFn = createServerFn({ method: "POST" })
  .inputValidator((data: { id: string; payload: any }) => data)
  .handler(async ({ data }): Promise<{ commonContact: CommonContactResponseReadable | {} }> => {
    const config = await createAuthenticatedApiConfig();
    const response = await createCommonContact({ ...config, body: data.payload.data, path: { id: Number(data.id) } });
    if (!response.data) {
      return await throwApiError(response, "Failed to create common contact", response.response?.status);
    }
    return { commonContact: response.data || {} };
  });

export const updateCommonContactFn = createServerFn({ method: "POST" })
  .inputValidator((data: { orgId: string; payload: any }) => data)
  .handler(async ({ data }): Promise<{ commonContact: CommonContactResponseReadable | {} }> => {
    const config = await createAuthenticatedApiConfig();
    const { id: contactId, ...bodyPayload } = data.payload;
    const response = await updateCommonContact({
      ...config,
      path: { id: Number(data.orgId), contactId: Number(contactId) },
      body: bodyPayload,
    });
    if (!response.data) {
      return await throwApiError(response, "Failed to update common contact", response.response?.status);
    }
    return { commonContact: response.data || {} };
  });

export const createServiceContactFn = createServerFn({ method: "POST" })
  .inputValidator((data: { orgId: string; payload: any }) => data)
  .handler(async ({ data }): Promise<{ serviceContact: ServiceContactResponseReadable | {} }> => {
    const config = await createAuthenticatedApiConfig();
    const response = await createServiceContact({ ...config, body: data.payload, path: { id: Number(data.orgId) } });
    if (!response.data) {
      return await throwApiError(response, "Failed to create service contact", response.response?.status);
    }
    return { serviceContact: response.data || {} };
  });

export const updateServiceContactFn = createServerFn({ method: "POST" })
  .inputValidator((data: { orgId: string; payload: any }) => data)
  .handler(async ({ data }): Promise<{ serviceContact: ServiceContactResponseReadable | {} }> => {
    const config = await createAuthenticatedApiConfig();
    // Extract contactId and remove it from payload
    const { id: contactId, ...bodyPayload } = data.payload;
    const response = await updateServiceContact({
      ...config,
      path: { id: Number(data.orgId), contactId: Number(contactId) },
      body: bodyPayload,
    });
    if (!response.data) {
      return await throwApiError(response, "Failed to update service contact", response.response?.status);
    }
    return { serviceContact: response.data || {} };
  });

export const deleteServiceContactFn = createServerFn({ method: "POST" })
  .inputValidator((data: { orgId: string; contactId: string }) => data)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await deleteServiceContact({
      ...config,
      path: { id: Number(data.orgId), contactId: Number(data.contactId) },
    });
    if (!response.data) {
      return await throwApiError(response, "Failed to delete service contact", response.response?.status);
    }
    return { success: true };
  });

export const deleteCommonContactFn = createServerFn({ method: "POST" })
  .inputValidator((data: { orgId: string; contactId: string }) => data)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await deleteCommonContact({
      ...config,
      path: { id: Number(data.orgId), contactId: Number(data.contactId) },
    });
    if (!response.data) {
      return await throwApiError(response, "Failed to delete common contact", response.response?.status);
    }
    return { success: true };
  });

export const updatePredefinedTextsFn = createServerFn({ method: "POST" })
  .inputValidator((data: { orgId: number; payload: any }) => data)
  .handler(async ({ data }): Promise<{ predefinedTexts: PredefinedTextsResponse | {} }> => {
    const config = await createAuthenticatedApiConfig();
    const response = await updateOrganisationPredefinedTexts({
      ...config,
      body: data.payload,
      path: { id: Number(data.orgId) },
    });
    if (!response.data) {
      return await throwApiError(response, "Failed to update predefined texts", response.response?.status);
    }
    return { predefinedTexts: response.data?.data || {} };
  });

export const deleteOrganisationFn = createServerFn({ method: "POST" })
  .inputValidator((data: { orgId: number }) => data)
  .handler(async ({ data }): Promise<{ success: boolean }> => {
    const config = await createAuthenticatedApiConfig();
    const response = await deleteOrganisation({ ...config, path: { id: data.orgId } });
    if (!response.data) {
      return await throwApiError(response, "Failed to delete organisation", response.response?.status);
    }
    return { success: true };
  });

export const updateOrganisationFn = createServerFn({ method: "POST" })
  .inputValidator((data: { orgId: number; payload: any }) => data)
  .handler(async ({ data }): Promise<{ organisation: OrganisationReadable }> => {
    const config = await createAuthenticatedApiConfig();
    const response = await updateOrganisation({ ...config, path: { id: Number(data.orgId) }, body: data.payload });
    if (!response.data) {
      return await throwApiError(response, "Failed to update organisation", response.response?.status);
    }
    return { organisation: response.data?.data || {} };
  });

export const organisationSettingsQueryOptions = (id: number) => ({
  queryKey: ["organisation-settings", id],
  queryFn: () => fetchOrganisationSettingsFn({ data: { orgId: id } }),
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: (query: any) => (isForbiddenError(query.state.error) ? false : "always"),
  refetchOnReconnect: false,
  retry: false,
});

export const updateOrganisationSettingsFn = createServerFn({ method: "POST" })
  .inputValidator((data: { orgId: number; payload: any }) => data)
  .handler(async ({ data }): Promise<{ organisationSettings: SettingListResponse }> => {
    const config = await createAuthenticatedApiConfig();
    const response = await updateOrganisationSettings({
      ...config,
      path: { id: data.orgId },
      body: data.payload,
    });
    if (!response.data) {
      return await throwApiError(response, "Failed to update organisation settings", response.response?.status);
    }
    return { organisationSettings: response.data || {} };
  });

export const resetOrganisationSettingsFn = createServerFn({ method: "POST" })
  .inputValidator((data: { orgId: number }) => data)
  .handler(async ({ data }): Promise<void> => {
    const config = await createAuthenticatedApiConfig();
    const response = await deleteAllOrganisationSettings({ ...config, path: { id: data.orgId } });
    if (!response.data) {
      return await throwApiError(response, "Failed to reset organisation settings", response.response?.status);
    }
    return;
  });

export const getOrganisationCallscreeningFn = createServerFn({ method: "GET" })
  .inputValidator((data: { orgId: number }) => data)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await getOrganisationCallscreening({ ...config, path: { id: Number(data.orgId) } });
    if (!response.data) {
      return await throwApiError(
        response,
        "Failed to get organisation callscreening rulesets",
        response.response?.status
      );
    }
    return response.data?.data || {};
  });

export const organisationCallscreeningQueryOptions = (orgId: number) => ({
  queryKey: ["organisation-callscreening", orgId],
  queryFn: () => getOrganisationCallscreeningFn({ data: { orgId } }),
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: (query: any) => (isForbiddenError(query.state.error) ? false : "always"),
  refetchOnReconnect: false,
  retry: false,
});

export const updateOrganisationCallscreeningFn = createServerFn({ method: "POST" })
  .inputValidator((data: { orgId: number; payload: any }) => data)
  .handler(async ({ data }): Promise<CallscreeningResponse["data"]> => {
    const config = await createAuthenticatedApiConfig();
    const response = await updateOrganisationCallscreening({
      ...config,
      body: data.payload,
      path: { id: Number(data.orgId) },
    });
    if (!response.data) {
      return await throwApiError(response, "Failed to update organisation callscreening", response.response?.status);
    }
    return response.data.data;
  });

export const testOrganisationCallscreeningFn = createServerFn({ method: "POST" })
  .inputValidator((data: { orgId: number; payload: any }) => data)
  .handler(async ({ data }): Promise<CallscreeningTestResponse["data"]> => {
    const config = await createAuthenticatedApiConfig();
    const response = await testOrganisationCallscreening({
      ...config,
      body: data.payload,
      path: { id: Number(data.orgId) },
    });
    if (!response.data) {
      return await throwApiError(response, "Failed to test callscreening", response.response?.status);
    }
    return response.data.data;
  });

export const updateOrganisationUserLabelsFn = createServerFn({ method: "POST" })
  .inputValidator((data: { orgId: number; payload: UserLabelList }) => data)
  .handler(async ({ data }): Promise<{ organisationUserLabels: UserLabelList }> => {
    const config = await createAuthenticatedApiConfig();
    const response = await updateOrganisationUserLabels({ ...config, path: { id: data.orgId }, body: data.payload });
    if (!response.data) {
      return await throwApiError(response, "Failed to update organisation user labels", response.response?.status);
    }
    return { organisationUserLabels: response.data?.data || [] };
  });

export const clearOrganisationOverrideFn = createServerFn({ method: "POST" })
  .inputValidator((data: { id: number; setting: string }) => data)
  .handler(async ({ data }): Promise<{ success: boolean }> => {
    const config = await createAuthenticatedApiConfig();
    const response = await deleteOrganisationSetting({ ...config, path: { name: data.setting, id: data.id } });
    if (!response.data) {
      return await throwApiError(response, "Failed to clear organisation override", response.response?.status);
    }
    return { success: true };
  });
